import React, { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { USER_STATUS_QUERY } from 'Queries/Queries';
import { format, parse } from 'date-fns';
import { setNestedObjectValues } from 'formik';
import { useInstallerStatusSummary } from 'hooks/UseInstallerStatusSummary';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormPageWrapper } from 'components/FormHelper/FormPageWrapper';
import { FormSection } from 'components/FormHelper/FormSection';
import { HintText } from 'components/HintText/HintText';
import { PlusIcon } from 'components/Icons/PlusIcon';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { NEFIExpertTable } from 'components/NEFIComponents/NEFIExpertsTable/NEFIExpertsTable';
import { NEFINewExpertPopup } from 'components/NEFIComponents/NEFINewExpertPopup/NEFINewExpertPopup';
import { NEFI_ERROR } from 'components/NEFIForm/Utils/NEFIError';
import { PageTitle } from 'components/PageTitle/PageTitle';

import { DEB_ACTIVE_PAGE } from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';

import { FFormDebug } from 'utils/FFormDebug';
import { checkKeyDown } from 'utils/FormUtils';

import { InstallerContactSection } from './InstallerContactSection';
import { SectionContent } from './SectionContent';
import { getDebFIBranchSelectionValues } from './Utils/DebFIFormBranchUtil';
import { DEBFI_FIRST_PAGE_SECTIONS, checkDebFIErrors } from './Utils/DebFIFormSectionUtil';
import { ERRORS } from './Utils/Errors';

import './DebForm.sass';

export const DebFIFirstPage = ({ formik, initialValues, installer, setPage, comboBoxOptions }) => {
  const [buttonErrorMessage, setButtonErrorMessage] = useState(ERRORS.DEFAULT);
  const [errors, setErrors] = useState([]);
  const [newExpertPopupOpen, setNewExpertPopupOpen] = useState(false);
  const [savedExpertsData, setSavedExpertsData] = useState(
    initialValues.experts ? initialValues.experts : []
  );
  const [savedInstallerContactValues, setSavedInstallerContactValues] = useState({
    phone: initialValues.phone,
    mobile: initialValues.mobile,
    email: initialValues.email,
  });

  const { values, errors: formErrors, setTouched, touched } = formik;
  const installerStatusSummary = useInstallerStatusSummary(installer);
  const branchSelectionValues = useMemo(
    () => getDebFIBranchSelectionValues(installerStatusSummary),
    [installerStatusSummary]
  );

  const { data: userStatusData, loading: userStatusLoading } = useQuery(USER_STATUS_QUERY, {
    gcTime: 0,
    fetchPolicy: 'network-only',
  });
  const userStatus = userStatusData && userStatusData.userStatus;

  useEffect(() => {
    setTouched(setNestedObjectValues(formErrors, true));
  }, [formErrors]);

  useEffect(() => {
    const effectiveInitialValues = {
      ...formik.initialValues,
      ...savedInstallerContactValues,
      experts: savedExpertsData,
    };
    const validationErrors = [];
    if (Object.keys(formErrors).length !== 0) {
      validationErrors.push(NEFI_ERROR.FORM_FIELD);
    }
    if (values.experts.length === 0) {
      validationErrors.push(NEFI_ERROR.MIN_EXPERT);
    }
    if (branchSelectionValues.isEnergyActive && branchSelectionValues.isGasActive) {
      const hasEnergyExpert = values.experts.some((expert) => expert.isExpertEnergyActive);
      const hasGasExpert = values.experts.some((expert) => expert.isExpertGasActive);
      if (!hasEnergyExpert || !hasGasExpert) {
        validationErrors.push(NEFI_ERROR.MIN_BRANCH_EXPERT);
      }
    }
    setErrors(validationErrors);

    const error = checkDebFIErrors(
      values,
      effectiveInitialValues,
      formErrors,
      branchSelectionValues
    );
    setButtonErrorMessage(error);
  }, [
    values,
    formErrors,
    savedInstallerContactValues,
    savedExpertsData,
    branchSelectionValues,
    formik.initialValues,
  ]);

  const cleanedDateString =
    userStatus &&
    userStatus.latestDebFormRequestDate &&
    userStatus.latestDebFormRequestDate.replace('T', ' ');

  if (userStatusLoading) {
    return <LoadingIndicator />;
  }

  return (
    <form
      onKeyDown={checkKeyDown}
      onSubmit={(e) => {
        e.preventDefault();
      }}
      className="deb-form__first-page">
      <FormPageWrapper>
        <div className="deb-form__title-section">
          <PageTitle text="Registrierung als Fachinteressent" />
          <div>
            Hier können Sie Ihre in unserem Verteiler Fachinteressenten hinterlegten Daten
            bearbeiten / aktualisieren.
          </div>
          <br />
          {cleanedDateString && (
            <HintText>
              <div>
                Ihre letzte Anfrage wurde gesendet am{' '}
                <b>
                  {format(
                    parse(cleanedDateString, 'yyyy-MM-dd HH:mm:ss.SSSSSS', new Date()),
                    'dd.MM.yyyy'
                  )}{' '}
                </b>
                um{' '}
                <b>
                  {format(
                    parse(cleanedDateString, 'yyyy-MM-dd HH:mm:ss.SSSSSS', new Date()),
                    'HH:mm'
                  )}{' '}
                  Uhr
                </b>
              </div>
            </HintText>
          )}
        </div>
        {DEBFI_FIRST_PAGE_SECTIONS.map((section) => {
          const evaluateConditionOptions = {
            installerStatusSummary,
            values,
          };
          if (!evaluateCondition(section.whenShouldSectionRender, evaluateConditionOptions)) {
            return null;
          }
          if (section.isInstallerContact) {
            return (
              <InstallerContactSection
                key="installerContact"
                formik={formik}
                onSaved={(vals) =>
                  setSavedInstallerContactValues((prev) => ({
                    ...prev,
                    ...vals,
                  }))
                }
                initialValues={initialValues}
              />
            );
          }
          return (
            <FormSection
              key={section.id}
              title={section.title}
              subtitle={section.subtitle}
              tooltip={section.tooltip}
              navigationTooltip={section.navigationTooltip}
              infoText={section.infoText}>
              <SectionContent
                section={section}
                formik={formik}
                values={values}
                initialValues={initialValues}
                comboBoxOptions={comboBoxOptions}
                evaluateConditionOptions={evaluateConditionOptions}
              />
            </FormSection>
          );
        })}
        <FormSection title="Fachinteressenten">
          <NEFIExpertTable
            formik={formik}
            errors={errors}
            branchSelectionValues={branchSelectionValues}
          />
          <ActionButton
            inverted
            icon={<PlusIcon />}
            title={<div className="text-nowrap"> Fachinteressent hinzufügen</div>}
            onClick={() => {
              setNewExpertPopupOpen(true);
            }}
          />
          <NEFINewExpertPopup
            branchSelectionValues={branchSelectionValues}
            popupOpen={newExpertPopupOpen}
            setPopupOpen={setNewExpertPopupOpen}
            mainFormik={formik}
          />
        </FormSection>
      </FormPageWrapper>
      <FormActionButtons
        hasErrors={Boolean(buttonErrorMessage)}
        titleLeft="Zurücksetzen"
        titleRight="Weiter"
        errorMessage={buttonErrorMessage}
        onClickLeft={() => {
          formik.resetForm();
          window.scrollTo(0, 0);
        }}
        onClickRight={() => {
          setPage(DEB_ACTIVE_PAGE.FORM_UPLOAD);
        }}
      />
      <FFormDebug values={values} errors={formErrors} touched={touched} />
    </form>
  );
};

DebFIFirstPage.displayName = 'DebFIFirstPage';

DebFIFirstPage.propTypes = {
  formik: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  installer: PropTypes.object.isRequired,
  setPage: PropTypes.func.isRequired,
  comboBoxOptions: PropTypes.shape({
    craftChambers: PropTypes.array,
    gridnetProviders: PropTypes.array,
  }),
};

DebFIFirstPage.defaultProps = {
  comboBoxOptions: {
    craftChambers: [],
    gridnetProviders: [],
  },
};
